import { useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { errorText } from '../../i18n';
import { CenterScreen, Logo, Spinner } from '@vatman/ui';
import './auth.css';

type Level = 'write' | 'read' | 'none';
const LEVELS: Level[] = ['write', 'read', 'none'];

/** What the app asked for (`scope`), as a starting choice; nothing asked means everything. */
function asked(scope: string | null, module: 'boards' | 'tracker'): Level {
  if (!scope) return 'write';
  const s = scope.split(/\s+/);
  if (s.includes(`${module}:write`)) return 'write';
  if (s.includes(`${module}:read`)) return 'read';
  return 'none';
}

/**
 * The consent page of the OAuth connection (crates/server/src/mcp/oauth.rs): which app asks,
 * where the answer goes, and what it may do — boards and issues separately. The agent acts as
 * the person and sees only what they see.
 */
export function ConnectPage() {
  const { t } = useTranslation();
  const session = useSession();
  const location = useLocation();
  const [params] = useSearchParams();
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';
  const [boards, setBoards] = useState<Level>(asked(params.get('scope'), 'boards'));
  const [tracker, setTracker] = useState<Level>(asked(params.get('scope'), 'tracker'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const info = useQuery({
    queryKey: ['oauth-client', clientId, redirectUri],
    queryFn: () => api.get<{ name: string; redirectHost: string }>(`/oauth/client?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}`),
    enabled: !!session.data?.user && !!clientId && !!redirectUri,
    retry: false,
  });

  if (session.isLoading) {
    return (
      <CenterScreen>
        <Spinner />
      </CenterScreen>
    );
  }
  if (!session.data?.user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  const answer = async (allow: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ redirect: string }>('/oauth/approve', {
        clientId,
        redirectUri,
        codeChallenge: params.get('code_challenge'),
        state: params.get('state'),
        ...(allow ? { boards, tracker } : {}),
      });
      window.location.assign(r.redirect);
    } catch (e) {
      setError(errorText(e instanceof ApiError ? e.code : 'internal'));
      setBusy(false);
    }
  };

  return (
    <div className="consent-gate">
      <div className="auth-card">
        <div className="connect-logos">
          <Logo size={34} />
          <span className="connect-dots" aria-hidden="true">
            ···
          </span>
          <span className="connect-app">
            <Bot size={20} />
          </span>
        </div>
        {info.isLoading ? (
          <Spinner />
        ) : info.isError || !info.data ? (
          <p className="error-text">{t('connect.unknown')}</p>
        ) : (
          <>
            <h2>{t('connect.title', { name: info.data.name })}</h2>
            <p className="muted" style={{ margin: 0 }}>
              {t('connect.text', { name: session.data.user.name })}
            </p>
            {(['boards', 'tracker'] as const).map((m) => (
              <label key={m} className="field connect-row">
                <span>{t(m === 'boards' ? 'tokens.moduleBoards' : 'tokens.moduleTracker')}</span>
                <select className="input" value={m === 'boards' ? boards : tracker} onChange={(e) => (m === 'boards' ? setBoards : setTracker)(e.target.value as Level)}>
                  {LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {t(`tokens.level.${l}`)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <p className="subtle" style={{ fontSize: 'var(--fs-2)', margin: 0 }}>
              {t('connect.back', { host: info.data.redirectHost })}
            </p>
            {error && <div className="error-text">{error}</div>}
            <button className="btn primary lg block" disabled={busy || (boards === 'none' && tracker === 'none')} onClick={() => void answer(true)}>
              {t('connect.allow')}
            </button>
            <button className="btn lg block" disabled={busy} onClick={() => void answer(false)}>
              {t('connect.deny')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
